// PetStatsPanel — resource bars (HP, Mana) on top + stat tile grid
// below. HP and Mana scale with level; current values come from
// kid_pet_state (migration 0247).
//
// As of migration 0264 the FIGHT STATS are time-windowed: each card
// the kid spends adds +1 to its matching stat for 3 hours, then
// fades. The panel reads `activeStats` off useKidPetState and
// surfaces a decay countdown under the grid.
//
// Lives under the KidCardDeck on the Pet tab.
//
// EMOJI-IN-UI EXCEPTION: this tree (src/experiments/kids-mode/**)
// is the explicit opt-in to emojis per the plan.

import { COLOR, FONT } from "../../lib/tokens";
import {
  STAT_DEFS, statsFromPet, hpFromPet, manaFromPet, decayLabel,
} from "./petStats";

export default function PetStatsPanel({ pet, activeStats }) {
  if (!pet) return null;
  const stats = statsFromPet(pet, activeStats);
  const hp = hpFromPet(pet);
  const mana = manaFromPet(pet);
  const buffActive = !!activeStats?.earliestAt;
  const fadeLabel = decayLabel(activeStats?.earliestAt);

  return (
    <div style={{
      marginTop: 18,
      width: "100%",
      maxWidth: 360,
      display: "flex",
      flexDirection: "column",
      gap: 10,
    }}>
      {/* HP + MANA bars. HP first (more central to the fight),
          Mana below. Both show current / max in mono. */}
      <ResourceBar
        emoji="❤️"
        label="HP"
        current={hp.current}
        max={hp.max}
        fill="linear-gradient(90deg, #E84A5F 0%, #FF8A3D 100%)"
        track="#FCEAEA"
      />
      <ResourceBar
        emoji="💧"
        label="MANA"
        current={mana.current}
        max={mana.max}
        fill="linear-gradient(90deg, #5DA9E9 0%, #3D6BB8 100%)"
        track="#E6F0FA"
      />

      <div style={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        marginTop: 6,
      }}>
        <span style={{
          fontFamily: FONT.mono,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.16em",
          color: COLOR.muted,
        }}>
          FIGHT STATS
        </span>
        {buffActive && fadeLabel && (
          <span style={{
            fontFamily: FONT.mono,
            fontSize: 10,
            letterSpacing: "0.08em",
            color: "#D96B2B",
            fontWeight: 700,
          }}>
            {fadeLabel}
          </span>
        )}
      </div>
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: 8,
      }}>
        {STAT_DEFS.map((s) => {
          const value = stats[s.key] ?? 0;
          const active = value > 0;
          return (
            <div
              key={s.key}
              title={s.longLabel}
              style={{
                position: "relative",
                padding: "10px 10px 12px",
                background: "#fff",
                border: `1.5px solid ${active ? s.tint : "#0001"}`,
                borderRadius: 12,
                display: "flex",
                flexDirection: "column",
                gap: 6,
                overflow: "hidden",
                opacity: active ? 1 : 0.6,
              }}
            >
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}>
                <span style={{ fontSize: 22, lineHeight: 1 }}>{s.emoji}</span>
                <span style={{
                  fontFamily: FONT.mono,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  color: active ? s.tint : COLOR.muted,
                }}>
                  {s.label}
                </span>
                <span style={{
                  marginLeft: "auto",
                  fontFamily: FONT.itemTitle,
                  fontStyle: "italic",
                  fontSize: 20,
                  color: COLOR.ink,
                  fontVariantNumeric: "tabular-nums",
                }}>
                  +{value}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      {!buffActive && (
        <div style={{
          marginTop: 4,
          fontFamily: FONT.forma,
          fontSize: 11,
          color: COLOR.muted,
          textAlign: "center",
        }}>
          Eat real food to power up — buffs last 3 hours.
        </div>
      )}
    </div>
  );
}

function ResourceBar({ emoji, label, current, max, fill, track }) {
  const pct = max > 0 ? Math.max(0, Math.min(1, current / max)) : 0;
  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      gap: 4,
    }}>
      <div style={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        fontFamily: FONT.mono,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.08em",
        color: COLOR.ink,
      }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 14 }}>{emoji}</span>
          <span>{label}</span>
        </span>
        <span style={{ color: COLOR.muted, fontSize: 10, fontVariantNumeric: "tabular-nums" }}>
          {current} / {max}
        </span>
      </div>
      <div style={{
        width: "100%",
        height: 10,
        background: track,
        border: `1px solid rgba(0,0,0,0.06)`,
        borderRadius: 999,
        overflow: "hidden",
      }}>
        <div style={{
          width: `${Math.round(pct * 100)}%`,
          height: "100%",
          background: fill,
          transition: "width 360ms cubic-bezier(0.34, 1.56, 0.64, 1)",
        }} />
      </div>
    </div>
  );
}
